// ─────────────────────────────────────────────────────────────────────────────
// Normalises a decrypted GeoComply response into the signals a tester cares
// about when EVALUATING BrowserGuard: which sources located you, their accuracy,
// the VPN/proxy score, and which anti-fraud rules fired.
//
// Rule names are taken verbatim from the engine's RulesManager (gc-engine). The
// authoritative "what did BrowserGuard catch" comes from error_details (the
// failed rules) and the troubleshooter rule names; error_summary gives the
// high-level category.
// ─────────────────────────────────────────────────────────────────────────────

export const RULE_LABELS = {
  proxy: 'VPN / Proxy',
  device_proxy: 'Device proxy',
  ip_filter: 'IP blocklist',
  boundary: 'Out of boundary',
  boundary_acc: 'Boundary accuracy',
  spoofing_detection: 'Location spoofing',
  mocked_location: 'Mocked location',
  simulated_location: 'Simulated location',
  root: 'Rooted device',
  virtual_machine: 'Virtual machine',
  advanced_virtual_machine: 'Virtual machine (adv.)',
  developer_mode: 'Developer mode',
  service_consumer: 'Remote-access software',
  blocked_software: 'Blocked software',
  installed_mock_apps: 'Mock-location apps',
  teleporters: 'Impossible travel',
  long_range_location: 'Long-range location',
  enhanced_long_range: 'Long-range (enhanced)',
  inconsistent_data: 'Inconsistent data',
  inconsistent_border_data: 'Inconsistent border data',
  delayed_submission: 'Delayed submission',
  in_flight_wifi_detection: 'In-flight Wi-Fi',
  device: 'Device blocklist',
  gps_acc: 'GPS accuracy',
  wifi_acc: 'Wi-Fi accuracy',
  gsm_acc: 'GSM accuracy',
  ip_acc: 'IP accuracy',
};

const num = (v) => {
  if (v === undefined || v === null || v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

function ruleDetail(rule, entry, troubleshooter) {
  const a = (entry && entry.attrs) || {};
  if (rule === 'proxy' || rule === 'device_proxy') {
    return [a.type, a.name, a.score].filter(Boolean).join(' · ') || null;
  }
  if (rule === 'boundary') {
    return [a.type, a.name, a.description].filter(Boolean).join(' · ') || null;
  }
  if (rule === 'device') {
    return [a.type, a.value].filter(Boolean).join('=') || null;
  }
  if ((rule === 'root' || rule === 'service_consumer' || rule === 'virtual_machine') && entry) {
    if (entry.items && entry.items.length) return entry.items.join(', ');
    if (a.title) return a.title;
  }
  // Fall back to the matching troubleshooter message.
  const msg = (troubleshooter || []).find((t) => t.rule === rule && t.content);
  return msg ? msg.content : (entry && entry.value) || null;
}

/**
 * @param {object} event - a debug-log event ({ raw, troubleshooter, error_code, ... })
 * @returns normalised signals, or null if there's no decrypted payload.
 */
export function extractSignals(event) {
  if (!event) return null;
  const raw = event.raw || {};
  const ed = raw.error_details || {};
  const troubleshooter = event.troubleshooter || raw.troubleshooter || [];

  // Which rules fired = keys of error_details ∪ troubleshooter rule names.
  const firedRules = new Set([
    ...Object.keys(ed),
    ...troubleshooter.map((t) => t && t.rule).filter((r) => r && r !== 'unknown'),
  ]);

  const risks = [...firedRules].map((rule) => ({
    rule,
    label: RULE_LABELS[rule] || rule,
    detail: ruleDetail(rule, ed[rule], troubleshooter),
  }));

  const SRC = ['gps', 'wifi', 'ip', 'gsm'];
  const sources = SRC.filter((s) => raw[s] && typeof raw[s] === 'object').map((s) => {
    const n = raw[s];
    const base = {
      type: s,
      lat: num(n.lat),
      lon: num(n.lon),
      acc: num(n.acc),
      err: num(n.err),
      primary: n.primary === 1 || n.primary === '1',
      city: n.city || null,
      region: n.region_code || n.region || null,
    };
    if (s === 'ip') {
      base.ipaddress = n.ipaddress || n.address || null;
      base.proxyscore = num(n.proxyscore);
      base.isp = n.isp || null;
    }
    return base;
  });

  const primary = sources.find((s) => s.primary)?.type
    || (raw.gps ? 'gps' : raw.wifi ? 'wifi' : raw.ip ? 'ip' : null);

  const errorCode = event.error_code != null ? Number(event.error_code) : (raw.error_code != null ? Number(raw.error_code) : null);
  const verdict = errorCode === 0 ? 'pass' : errorCode === 1 ? 'denied' : (errorCode == null ? 'unknown' : 'error');

  const ip = sources.find((s) => s.type === 'ip');

  return {
    verdict,
    errorCode,
    errorMessage: event.error_message || raw.error_message || null,
    sources,
    primary,
    proxyscore: ip?.proxyscore ?? null,
    ipAddress: event.ip || ip?.ipaddress || raw.ipAddress || null,
    isp: event.isp || ip?.isp || raw.isp || null,
    connType: raw.conn_type || null,
    accuracy: num(event.raw?.accuracy) ?? num(raw.gps?.acc) ?? num(raw.wifi?.acc) ?? null,
    city: event.city || raw.city || null,
    state: event.state || raw.state_code || null,
    lat: num(event.lat) ?? num(raw.latitude),
    lon: num(event.lon) ?? num(raw.longitude),
    risks,
    summary: raw.error_summary || null,
  };
}

// Great-circle distance (metres) between two lat/lon points — for A/B location deltas.
export function haversineMeters(a, b) {
  if (a?.lat == null || a?.lon == null || b?.lat == null || b?.lon == null) return null;
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(s)));
}

export function formatDistance(m) {
  if (m == null) return '—';
  if (m < 1000) return `${m} m`;
  return `${(m / 1000).toFixed(m < 10000 ? 2 : 0)} km`;
}
